<script setup generic="TData extends Record<string, unknown>" lang="ts">
import { writeFileXLSX } from 'xlsx'

/**
 * Interface representing an additional sheet in the Excel export
 */
interface SheetExport {
  /** Maps data object keys to human-readable column headers */
  mapping: Record<string, string>
  /** Array of data objects to be exported in this sheet */
  data: Record<string, unknown>[]
  /** Name of the sheet in the Excel workbook */
  name: string
}

const props = defineProps<{
  /** Optional file name, is prepended to sheet name If not provided, defaults to sheetName. A timestamp and .xlsx extension will be appended automatically.*/
  fileName?: string

  /** Main data to be exported
   * Array<string> will be converted to comma separated strings,
   * but arrays of objects can't be handled and need to be formatted before passing to this component
   */
  data: TData[]

  /** Optional details sheet prepended before other sheets to export, use for adding additional context to the export */
  details?: SheetExport

  /** A key value pair where the key is the key or json pointer to TData fields and the value is the column label
   *
   * Column order is determined by the order of the keys in the object
   *
   * @example
   * { name: 'Entity Name', '/insights/count_total': 'Total' }
   *
   * @example
   * const data = [{ name: 'John', favorites: { color: 'Blue' } }, { name: 'Paul', favorites: { color: 'Red' } }]
   * const mapping = { name: 'Their Name', '/favorites/color': 'Favorite Colo
   */
  mapping: Record<keyof TData | string, string>

  /** Sheet name used for the exported XLSX */
  sheetName: string

  /** Optional supplemental prepended sheets to export, use for adding additional context to the export */
  supplemental?: SheetExport[]

  /** Set to true if additional data is being fetched */
  fetchingAll?: boolean

  /** Optional error message when fetching data */
  fetchError?: string

  /** Optional total count of data to export */
  total?: number

  /** Optional data analytics tag use for pendo analytics */
  dataAnalyticsTag?: string

  /** When true, hides the built-in Export button (useful if parent controls activation) */
  hideButton?: boolean
}>()

const emit = defineEmits<{
  'fetch-all': []
  'export-complete': []
}>()

const MAX_EXPORT_ROWS = 2000
enum ExportState {
  IDLE = 'idle',
  INIT = 'init',
  EXPORTING = 'exporting',
  FETCHING = 'fetching',
}
const exportState = ref(ExportState.IDLE)
const loading = ref(false)
const { isSupported } = useFileSystemAccess({
  types: [
    {
      description: 'text',
      accept: {
        'text/csv': ['.csv'],
        'application/vnd.ms-excel': ['.xls'],
      },
    },
  ],
  excludeAcceptAllOption: true,
})
const { $toast } = useNuxtApp()

const showDialog = ref(false)

const countGuard = () => {
  if (props.total && props.total > MAX_EXPORT_ROWS) {
    showDialog.value = true
    return
  }

  startExport()
}

const exportData = async (data: typeof props.data) => {
  loading.value = true

  try {
    const workbook = await exportToXlsxInWorker({
      data: JSON.stringify(data),
      sheetName: props.sheetName,
      mapping: props.mapping,
      supplemental: JSON.stringify(props.supplemental),
      details: JSON.stringify(props.details),
    })
    const timestamp = ` - Rialtic Export ${timezoneFormatter(undefined, {
      ...intlDateFormats.monDayYear,
      ...intlDateFormats.timeWithTimeZoneName,
    }).format(new Date())}`
    const fileName = `${props.fileName || props.sheetName}${timestamp}`
    writeFileXLSX(workbook, `${fileName}.xlsx`)
    resetState()

    $toast('Export will download momentarily', {
      icon: 'i-ph-circle-notch-bold animate-spin text-secondary h-5 w-5 inline-block',
      hideProgressBar: true,
      pauseOnFocusLoss: false,
    })

    emit('export-complete')
  } catch {
    resetState()
    $toast.error('There was a problem exporting the data')
  }
}
const toastId = ref()
const triggerToast = () => {
  $toast.clear()
  toastId.value = $toast(
    exportState.value === ExportState.FETCHING
      ? 'Gathering all data...'
      : 'Exporting data...',
    {
      closeButton: false,
      icon: 'i-ph-circle-notch-bold animate-spin text-secondary h-5 w-5 inline-block',
      draggable: false,
      hideProgressBar: true,
      pauseOnFocusLoss: false,
      timeout: false,
    },
  )
}

const dismissToast = () => {
  if (toastId.value) {
    $toast.dismiss(toastId.value)
    toastId.value = null
  }
}

const resetState = () => {
  exportState.value = ExportState.IDLE
  loading.value = false
  showDialog.value = false
  dismissToast()
  pause()
}

const fetchAllDebounced = useDebounceFn(() => {
  emit('fetch-all')
}, 600)

const { pause, resume, isActive } = watchPausable(
  [() => props.data, exportState, () => props.fetchError],
  async ([data, state, fetchError]) => {
    if (fetchError) {
      resetState()
      $toast.error(fetchError)
      return
    }

    if (state === ExportState.IDLE || data.length === 0) return
    if (props.total && data.length !== props.total) {
      fetchAllDebounced()
      exportState.value = ExportState.FETCHING

      // Prevent toast from popping each time a new page is requested
      if (!toastId.value) triggerToast()
      return
    }
    if (data.length > 500) {
      triggerToast()
    }

    pause()

    exportState.value = ExportState.EXPORTING
    await exportData(data)
  },
)

const cancelExport = () => {
  resetState()
}

const startExport = () => {
  if (exportState.value !== ExportState.IDLE) return
  if (!isActive.value) {
    resume()
  }
  loading.value = true
  exportState.value = ExportState.INIT
}

defineExpose({
  triggerExport: countGuard,
})

onBeforeUnmount(() => {
  cancelExport()
})
</script>

<template>
  <UiButton
    v-if="isSupported && !hideButton"
    class="bg-surface border-neutral-200 px-3 font-medium text-neutral-900"
    :disabled="total === 0"
    :loading="loading"
    size="xs"
    variant="outlined"
    :data-analytics="dataAnalyticsTag"
    @click="countGuard()"
  >
    <i
      class="i-ph-microsoft-excel-logo-bold vertical-bottom mr-2 inline-block h-4 w-4"
    />
    Export
  </UiButton>
  <UiDialog v-model="showDialog" max-width="420px" persistent>
    <h1 class="h2 p-2 px-4 text-neutral-800">Export to Excel</h1>
    <p class="body-1 px-4 text-neutral-900">
      Only the first {{ MAX_EXPORT_ROWS }} items will be exported.
    </p>
    <footer class="flex justify-end space-x-4 px-4 py-2">
      <UiButton size="sm" variant="text" @click="cancelExport">Cancel</UiButton>
      <UiButton
        :loading="loading"
        size="sm"
        variant="text"
        @click="startExport()"
      >
        Export
      </UiButton>
    </footer>
  </UiDialog>
</template>
